import * as PIXI from 'pixi.js';
import * as TweenJS from "slotworks/utils/tween";
import FreespinsWheelRecord from "integration/records/freespins-wheel-record";
import { BounceZoomIn, BounceZoomOut } from "slotworks/graphics/animation/base-animations";
import Layers from "slotworks/graphics/layers/layers";
import { AbstractSceneTransition } from "slotworks/graphics/layers/scene-transitions/abstract-scene-transition";
import gameState from "slotworks/model/game-state";
import Contract from "slotworks/utils/contract";
import Rectangle from "slotworks/utils/geom/rectangle";
import { parallel } from "slotworks/utils/race-utils";
import HarleyComponents from "components/components";
import { FadeInLayersCommand } from 'commands/fade-layers-command';
import soundManager from 'slotworks/sound/sound-manager';

export default class FreespinsTransitions extends AbstractSceneTransition {

    public in(): Contract<void> {
        return new Contract((resolve) => {
            parallel([
                () => BounceZoomIn([
                    Layers.Prompts.getSprite("freespins_title"),
                    Layers.Prompts.getSprite("value")
                ], 400),
                () => BounceZoomIn([
                    Layers.Prompts.getSprite("WITH")
                ], 450, 100),
                () => BounceZoomIn([
                    Layers.Prompts.getSprite("boosters"),
                    Layers.Prompts.getSprite("x"),
                    Layers.Prompts.getSprite("Harley_symbol")
                ], 450, 200),
                () => BounceZoomIn([
                    Layers.Prompts.getSprite("boosters_text_1"),
                    Layers.Prompts.getSprite("boosters_text_2")
                ], 450, 300),
                () => BounceZoomIn([
                    Layers.Prompts.getSprite("btn-continue"),
                    Layers.Prompts.getSprite("btn-continue-lbl"),
                ], 450, 400),
            ]).then(resolve);
        });
    }

    public out(): Contract<void> {
        return new Contract((resolve) => {
            Layers.Background.setScene("freespins").execute();
            const showGame = () => {
                // show layers
                FadeInLayersCommand([
                    Layers.MatrixBackground,
                    Layers.MatrixContent,
                    Layers.MatrixForeground,
                    Layers.SymbolAnimations,
                    Layers.BetBar,
                    Layers.SmartButton,
                    Layers.Banners,
                    Layers.Footer,
                    Layers.Custom.get("Messages"),
                    Layers.Custom.get("MatrixNeon"),
                    Layers.Custom.get("MatrixAnim"),
                ]).execute();

                HarleyComponents.harley.hide();

                soundManager.customEvent("bonus_music_stop");

                soundManager.customEvent("freespins_music");
            }
            const symbol = Layers.Prompts.getSprite("Harley_symbol");
            const startPosition = new Rectangle(symbol.x, symbol.y, symbol.width, symbol.height);
            const texture = symbol.texture;
            const funcs: (() => Contract<void>)[] = [];

            const record = gameState.getCurrentGame().getCurrentRecord() as FreespinsWheelRecord;

            const initialPositions = [
                1, // "Harley_symbol1_1",
                1, // "Harley_symbol2_1",
                2, // "Harley_symbol3_2",
                2,  // "Harley_symbol4_2",
                2,  // "Harley_symbol5_2",
            ];
            if (record.giftedAdditional > 0) {
                const boosters = record.giftedAdditional;
                const target = Layers.Prompts.getPosition("target");
                let shown = false;
                for (let i = 0; i < record.giftedAdditional; i++) {
                    const current = i + 1;
                    funcs.push(() => new Contract<void>(res => {
                        const sprite = new PIXI.Sprite(texture);
                        Layers.Prompts.add(sprite);
                        sprite.position.set(startPosition.x, startPosition.y);
                        sprite.width = startPosition.width;
                        sprite.height = startPosition.height;
                        const position = Layers.Prompts.getPosition("Harley_symbol" + boosters + "_" + current)
                        new TweenJS.Tween(sprite)
                            .to({ x: position.x, y: position.y }, 400)
                            .onComplete(() => {                                
                                if (boosters > 1 && current != initialPositions[boosters - 1]) {
                                    const midPosition = Layers.Prompts.getPosition("Harley_symbol" + boosters + "_" + initialPositions[boosters - 1]);
                                    const dx = sprite.x - midPosition.x;
                                    const dy = sprite.y - midPosition.y;
                                    const distance = Math.sqrt(dx * dx + dy * dy);
                                    new TweenJS.Tween(sprite)
                                        .to({ x: [midPosition.x, target.x], y: [midPosition.y, target.y], width: target.width, height: target.height }, 500 + distance)
                                        .easing(TweenJS.Easing.Cubic.In)
                                        .delay(distance + 100)
                                        .interpolation(TweenJS.Interpolation.Bezier)
                                        .onStart(() => {
                                            if (!shown) {
                                                shown = true;
                                                showGame();
                                            }
                                        })
                                        .delay(200 + (current - 1) * 60)
                                        .onComplete(() => {
                                            soundManager.customEvent("symbol_harley_fly_land");
                                            Layers.Prompts.remove(sprite);
                                            res(null);
                                        })
                                        .start()
                                } else {
                                    new TweenJS.Tween(sprite)
                                        .to({ x: target.x, y: target.y, width: target.width, height: target.height }, 500)
                                        .easing(TweenJS.Easing.Cubic.In)
                                        .onStart(() => {
                                            if (!shown) {
                                                shown = true;
                                                showGame();
                                            }
                                        })
                                        .delay(200 + (current - 1) * 60)
                                        .onComplete(() => {
                                            soundManager.customEvent("symbol_harley_fly_land");
                                            Layers.Prompts.remove(sprite);
                                            res(null);
                                        })
                                        .start()
                                }
                            })
                            .start();
                    }));
                }
            } else {
                showGame();
            }

            soundManager.customEvent("scatter_harley_spread");
            setTimeout(() => {
                parallel([
                    () => BounceZoomOut([
                        Layers.Prompts.getSprite("freespins_title"),
                        Layers.Prompts.getSprite("value")
                    ], 400, 700),
                    () => BounceZoomOut([
                        Layers.Prompts.getSprite("WITH")
                    ], 400),
                    () => BounceZoomOut([
                        Layers.Prompts.getSprite("boosters"),
                        Layers.Prompts.getSprite("x"),
                        Layers.Prompts.getSprite("Harley_symbol")
                    ], 400),
                    () => BounceZoomOut([
                        Layers.Prompts.getSprite("boosters_text_1"),
                        Layers.Prompts.getSprite("boosters_text_2")
                    ], 400),
                    () => BounceZoomOut([
                        Layers.Prompts.getSprite("Harlequins"),
                        Layers.Prompts.getSprite("btn-continue"),
                        Layers.Prompts.getSprite("btn-continue-lbl"),
                    ], 400),
                ]).then(() => {
                    const sprites = [
                        "freespins_title", "value",
                        "WITH",
                        "boosters", "x", "Harley_symbol",
                        "boosters_text_1",
                        "boosters_text_2",
                        "Harlequins"
                    ];
                    for (let sprite of sprites) {
                        Layers.Prompts.getSprite(sprite).visible = false;
                    }
                });

                
                parallel(funcs).then(() => {
                    HarleyComponents.quickHit.setFreespinsBoosts(record.giftedAdditional).then(() => {
                        resolve(null);
                    });
                });
            }, 100);
        });
    }
}
